import SubmissionFlowHeader from '../../components/SubmissionFlowHeader/SubmissionFlowHeader';
import { HeaderBreadcrumb, Link } from '@components/SubmissionFlowHeader/styles';
import * as Styled from './styles';
import SubmissionFlowFooter from '@components/SubmissionFlowFooter/SubmissionFlowFooter';
import { useParams, useNavigate } from 'react-router';
import { globalRoutes } from '@routing/routes';
import { StyledSVG } from '@common/styles';
import { useFeatureFlags } from '@common/hooks/useFeatureFlags';
import { useDownloadAndOpenFile } from '@common/hooks/useDownloadAndOpenFile';
import { StyledLink } from '@common/components/Link/styles';
import theme from '@common/theme';
import { useFetchSubmission } from '@common/features/submission/hooks/useFetchSubmission';
import { Html } from './Html';
import { useFetchTasks } from '@common/hooks/useFetchTasks';
import { Button, ButtonTypes } from '@common/components';
import { formatDocumentSubTitle } from '@common/utils/utils';
import { HtmlDocument } from '@common/components/HtmlDocument/HtmlDocument';
import { getSubmissionTaskDocument } from '@common/features/document/helpers';
import { DocumentType } from '@common/types';
import { SubmissionTaskStatuses } from '@common/services/types';

const staleTime = 10 * 1000 * 60;

interface Props {
  onContinue: () => void;
  showIds: boolean;
  printUrl: string;
  published?: boolean;
  htmlData?: string;
  isAdmin?: boolean;
}

export const ViewHtml = ({ onContinue, showIds, printUrl, published, htmlData, isAdmin = false }: Props) => {
  const navigate = useNavigate();
  const { showPrintButton } = useFeatureFlags();

  const { taskId = '', submissionId = '' } = useParams();
  const { submission } = useFetchSubmission(submissionId, staleTime);
  const { tasks } = useFetchTasks({ submissionId, staleTime, skipCompany: true });
  const submissionTask = tasks.find((t) => t.submissionTaskId.toString() === taskId);

  const document = getSubmissionTaskDocument(submission, submissionTask);

  const { downloadAndOpen } = useDownloadAndOpenFile();

  const submissionTaskName = `${submissionId}/${taskId}_${submission?.productGroup?.productGroupName?.replaceAll(' ', '_')}`;

  const handleFileClick = () => { downloadAndOpen({ submissionId, taskId: submissionTask?.submissionTaskId }); };

  const onPrint = () => {
    if (published) {
      window.open(printUrl.replace(':documentId', document!.documentId.toString())
        .replace(':documentType', document!.type.toString())
        .replace(':version', document!.version.toString())
        , '_blank')
    }
    window.open(printUrl.replace(':taskId', taskId), '_blank')
  }

  // The Print button is shown only if:
  // - the feature flag is enabled, and
  // - the user is an admin, or
  // - the document is not an SMPC, or
  // - the submission is in progress (for draft documents), or
  // - the document has TVT files
  const canPrint = showPrintButton && (
    isAdmin
    || (!!document ? document?.type !== DocumentType.Smpc : false)
    || (!!submissionTask ? submissionTask?.status === SubmissionTaskStatuses.WithClientApproval : false)
    || Boolean(document?.hasTvtFiles)
  );

  return (
    <>
      <Styled.Wrapper>
        <SubmissionFlowHeader title="View html" subTitle={formatDocumentSubTitle(showIds, document?.documentTitle, document, true)}>
          <HeaderBreadcrumb>
            <Link> Submissions</Link> &gt;
            <Link> Submission details</Link> &gt;
            <Link> {submissionTaskName}</Link> &gt;
            <Link> View html</Link>
          </HeaderBreadcrumb>
        </SubmissionFlowHeader>
        <Styled.HorizontalDivider />  
        <Styled.FullPagePopupBackground>
          <Styled.FullPageTabs>
            <Styled.SectionHeader>
              <StyledSVG src="/icons/html.svg" />
              <StyledLink onClick={handleFileClick} to="#">
                {document?.originalFileName}
              </StyledLink>
              {canPrint && 
                <Button
                  type={ButtonTypes.PRIMARY}
                  text="Print"
                  height={45}
                  width={170}
                  color={theme.elements.defaultWhite}
                  onClick={onPrint}
                  tooltip='Click here to see the html view, and print or generate a PDF. Please note that the PDF output will depend on the tool within your browser and there may be some differences between its presentation and how the document appears on emc - e.g. some special characters and table width may be rendered differently when generating a PDF using different browsers.'
                />
              }
            </Styled.SectionHeader>
            <Styled.SectionBody>
              {!published && htmlData &&
                <Html html={htmlData} docType={document?.type} />}
              {published &&
                <HtmlDocument
                  documentId={(document?.documentId ?? -1).toString()}
                  version={(document?.version ?? -1).toString()}
                  documentType={document?.type} />}
            </Styled.SectionBody>
          </Styled.FullPageTabs>
        </Styled.FullPagePopupBackground>
      </Styled.Wrapper>
      <SubmissionFlowFooter onContinue={onContinue} continueText="Continue" onCancel={() => navigate(globalRoutes.dashboard)} />
    </>
  );
};
